<script>
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iIcon,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
  iKeyIconSize,
} from '@inscribe/signal';
import { mapGetters } from 'vuex';
import {
  formatSignal,
  formatCompliance,
  getSignalTitle,
  getSignalSubtitle,
  customerHasAnyHighRiskDocuments,
} from './insight.utils';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import { RISK_LEVEL } from '@/constants/riskLevels';
import CustomerFraudSignalsDetails from '@/V2/customers/fraud/CustomerFraudSignalsDetails.vue';
import CustomerComplianceCheckDetails from '@/V2/customers/business-compliance-agent/CustomerComplianceCheckDetails.vue';
import { CUSTOMER_INSIGHT_CATEGORIES } from '@/utils/customerInsights';

export default {
  components: {
    iButton,
    ExpandableContainerItem,
    StatusBadge,
    iKeyIcon,
    iIcon,
    CustomerFraudSignalsDetails,
    CustomerComplianceCheckDetails,
  },
  data() {
    return {
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      loadingInsights: false,
      getSignalTitle,
      getSignalSubtitle,
      selectedInsight: null,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
      iKeyIconSize,
      RISK_LEVEL,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedCustomerSignalsById',
      'getCachedAggregatedDocumentResults',
    ]),
    allInsights() {
      return (
        this.getCachedCustomerSignalsById(this.$route.params.customer_id)
          ?.data || {}
      );
    },
    aggregatedDocumentsResults() {
      return this.getCachedAggregatedDocumentResults(
        this.$route.params.customer_id,
      );
    },
    categories() {
      return Object.keys(this.allInsights)
        .map((insight) => this.allInsights[insight].category)
        .filter((category) => category !== undefined);
    },
    insightsByCategory() {
      const insightsByCategory = {};
      for (let insightKey of Object.keys(this.allInsights || {})) {
        const rawInsight = this.allInsights[insightKey];
        if (!rawInsight.category) {
          rawInsight.category = 'Other';
        }
        if (!insightsByCategory[rawInsight.category]) {
          insightsByCategory[rawInsight.category] = [];
        }
        const insightObject = this.processInsight(insightKey, rawInsight);
        if (insightObject) {
          insightsByCategory[rawInsight.category].push(insightObject);
        }
      }
      return insightsByCategory;
    },
    sortedCategories() {
      const categories = Object.keys(CUSTOMER_INSIGHT_CATEGORIES)
        .sort((a, b) => {
          return (
            CUSTOMER_INSIGHT_CATEGORIES[a].order -
            CUSTOMER_INSIGHT_CATEGORIES[b].order
          );
        })
        .map((category) => CUSTOMER_INSIGHT_CATEGORIES[category].label);
      return [...categories, 'Other'];
    },
  },
  methods: {
    onBackButtonClick() {
      this.$router.push({
        name: 'customer-overview',
        params: { customer_id: this.$route.params.customer_id },
      });
    },
    processInsight(insightKey, rawInsight) {
      const formattedSignal = formatSignal(
        insightKey,
        rawInsight,
        customerHasAnyHighRiskDocuments(this.aggregatedDocumentsResults),
      );
      if (formattedSignal) {
        if (Object.keys(formattedSignal).length > 0) {
          return formattedSignal;
        }
      } else {
        return formatCompliance(rawInsight);
      }
    },
    getFlaggedCount(insights) {
      const flaggedCount = insights.filter(
        (insight) => insight.result === RISK_LEVEL.HIGH,
      ).length;
      return `${flaggedCount}/${insights.length} flagged`;
    },
    onInsightClick(insight) {
      this.selectedInsight = insight;
    },
    hideDrawer() {
      this.selectedInsight = null;
    },
  },
};
</script>

<template>
  <div class="customer-insights-page i-pb-11 i-flex">
    <div class="customer-insights-page-container d-flex flex-column">
      <!-- title -->
      <div class="title-container d-flex flex-row i-gap-6 i-px-11 i-py-8">
        <i-button
          left-icon="medium-arrows-left"
          :size="iButtonSizes.MEDIUM"
          :button-style="iButtonStyles.GHOST"
          :type="iButtonTypes.NEUTRAL"
          @button-click="onBackButtonClick"
        />
        <div class="title-text d-flex flex-column">
          <div class="title i-text-heading-6">Insights</div>
          <div class="subtitle i-text-body-2">
            All customer-level insights organized by category
          </div>
        </div>
      </div>

      <!-- content -->
      <div class="overview-content d-flex flex-column i-gap-8 i-px-11">
        <template v-for="category in sortedCategories" :key="category">
          <div
            class="category d-flex flex-column i-gap-5"
            v-if="
              insightsByCategory &&
              insightsByCategory[category] &&
              insightsByCategory[category].length > 0
            "
          >
            <div
              class="category-title-container d-flex flex-row align-items-center justify-content-between"
            >
              <div class="title i-text-button-2">{{ category }}</div>
              <div class="flagged-count i-text-body-2">
                {{ getFlaggedCount(insightsByCategory[category]) }}
              </div>
            </div>
            <div class="insight-list i-radius-2xl">
              <expandable-container-item
                v-for="insight in insightsByCategory[category]"
                :key="insight.name || insight.title"
                :title="getSignalTitle(insight)"
                :subtitle="getSignalSubtitle(insight)"
                class="insight-item i-radius-2xl"
                @on-item-click="onInsightClick(insight)"
                @on-right-icon-click="onInsightClick(insight)"
                data-testid="customer-fraud-signal-item"
              >
                <template #left-container>
                  <i-key-icon
                    v-if="insight.icon"
                    :icon="insight.icon"
                    :type="insight.iconType"
                    :icon-style="insight.iconStyle"
                    :use-solid-icon-colour="true"
                  ></i-key-icon>
                </template>

                <template #right-container>
                  <status-badge
                    :state="insight.status"
                    :label="insight.badgeLabel"
                  ></status-badge>
                </template>
              </expandable-container-item>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- details -->
    <!-- details view -->
    <customer-fraud-signals-details
      v-if="!loadingInsights && selectedInsight?._type === 'signal'"
      :iDrawerVisible="!!selectedInsight"
      :selected-signal="selectedInsight"
      :aggregated-documents-results="aggregatedDocumentsResults?.fraud_results"
      @hide-drawer="hideDrawer"
    />

    <!-- details drawer -->
    <customer-compliance-check-details
      v-if="!loadingInsights && selectedInsight?._type === 'business_check'"
      :open="!!selectedInsight"
      :check="selectedInsight"
      @close="hideDrawer"
    />
  </div>
</template>

<style lang="scss" scoped>
.customer-insights-page {
  .title-container {
    margin-bottom: 24px;
    border-bottom: 1px solid var(--border-neutral-subtle);

    .title {
      color: var(--text-neutral-bold);
    }

    .subtitle {
      color: var(--text-neutral-subtle);
    }
  }

  .category {
    .category-title-container,
    .flagged-count {
      color: var(--text-neutral-bold);
    }

    .insight-list {
      border: 1px solid var(--border-neutral-subtle);

      .insight-item {
        width: 100%;
        &:not(:last-child) {
          border-bottom: 1px solid var(--border-neutral-subtle);
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    }
  }
}
</style>
